/**
 * 下から引き上げる「状況」の引き出し（ADR-0055）。DOM の部分。状態は drawer-model.ts。
 * 取っ手（画面の下の細い線・引き出しの上の線）を押す／引き上げる／引き下げる、後ろの影を押す、Escape で開け閉め。
 * 中身は呼ぶ側が body に入れる（開くたびに onOpen で描き直す）。
 */
import { DrawerState, offsetWhileDragging } from "./drawer-model.js";

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Record<string, string> = {},
  ...children: (Node | string)[]
) {
  const e = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) e.setAttribute(k, v);
  e.append(...children);
  return e;
}

export interface Drawer {
  state: DrawerState;
  /** 画面の下の取っ手（会話の画面に置く） */
  handle: HTMLElement;
  /** 引き出しの中身（見出しの下）。呼ぶ側が入れる */
  body: HTMLElement;
  /** 引き出しの右上（日付・時刻） */
  meta: HTMLElement;
  /** 引き出しの一番下（読み上げ・ログアウト・止める） */
  foot: HTMLElement;
}

export function mountDrawer(app: HTMLElement, onOpen: () => void): Drawer {
  const state = new DrawerState();
  const handle = el("button", {
    class: "jv-handle",
    type: "button",
    "aria-label": "状況を開く",
    "aria-expanded": "false",
  });
  const grip = el("button", { class: "jv-grip", type: "button", "aria-label": "状況を閉じる" });
  const meta = el("div", { class: "jv-drawer-meta" });
  const body = el("div", { class: "jv-drawer-body" });
  const foot = el("div", { class: "jv-drawer-foot" });
  const sheet = el(
    "section",
    { class: "jv-drawer", role: "dialog", "aria-label": "状況", "aria-hidden": "true" },
    grip,
    el(
      "div",
      { class: "jv-drawer-head" },
      el("div", { class: "jv-latin jv-drawer-title" }, "STATUS"),
      meta,
    ),
    body,
    foot,
  );
  const backdrop = el("div", { class: "jv-backdrop", "aria-hidden": "true" });
  app.append(backdrop, sheet);

  state.subscribe((s) => {
    const h = sheet.offsetHeight || 1;
    if (s.dragging) {
      sheet.classList.add("dragging");
      sheet.style.transform = `translateY(${offsetWhileDragging(s.open, s.dy, h)}px)`;
      backdrop.style.opacity = String(1 - offsetWhileDragging(s.open, s.dy, h) / h);
    } else {
      sheet.classList.remove("dragging");
      sheet.style.transform = "";
      backdrop.style.opacity = "";
    }
    sheet.classList.toggle("open", s.open);
    backdrop.classList.toggle("open", s.open);
    sheet.setAttribute("aria-hidden", String(!s.open));
    handle.setAttribute("aria-expanded", String(s.open));
    handle.setAttribute("aria-label", s.open ? "状況を閉じる" : "状況を開く");
    app.classList.toggle("drawer-open", s.open);
  });

  let lastOpens = 0;
  state.subscribe((s) => {
    if (s.open && state.opens !== lastOpens) {
      lastOpens = state.opens;
      onOpen();
    }
  });

  // 取っ手: 押すと開け閉め、引くと追いかける
  const attachDrag = (target: HTMLElement) => {
    let startY = 0;
    let lastY = 0;
    let lastT = 0;
    let v = 0;
    let moved = false;
    target.addEventListener("pointerdown", (e) => {
      if (e.button !== 0) return;
      target.setPointerCapture(e.pointerId);
      startY = lastY = e.clientY;
      lastT = e.timeStamp;
      v = 0;
      moved = false;
      state.dragStart();
    });
    target.addEventListener("pointermove", (e) => {
      if (!state.snapshot.dragging) return;
      const dy = e.clientY - startY;
      if (Math.abs(dy) > 4) moved = true;
      const dt = e.timeStamp - lastT;
      if (dt > 0) v = (e.clientY - lastY) / dt;
      lastY = e.clientY;
      lastT = e.timeStamp;
      state.dragMove(dy);
    });
    const end = (e: PointerEvent) => {
      if (!state.snapshot.dragging) return;
      if (!moved) state.toggle();
      else state.dragEnd(v);
      e.preventDefault();
    };
    target.addEventListener("pointerup", end);
    target.addEventListener("pointercancel", () => state.dragCancel());
    // 押しただけのときの click は pointerup で処理済み
    target.addEventListener("click", (e) => e.preventDefault());
  };
  attachDrag(handle);
  attachDrag(grip);
  backdrop.onclick = () => state.setOpen(false);
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && state.open) state.setOpen(false);
  });

  return { state, handle, body, meta, foot };
}
